import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type {
  ClockInInput,
  EmployeeClientDetail,
  EmployeeClientListItem,
  EmployeeDashboard,
  EmployeeJobDetail,
  EmployeeJobListItem,
  EmployeeNotificationPreferenceInput,
  EmployeeNotificationPreferences,
  EmployeeProfileUpdateInput,
  EmployeeProfileView,
  EmployeeSchedule,
  OpenShift,
  ShiftView,
} from '@shared/schemas/employee-portal';
import { JOB_STATUS_LABELS, type JobStatusValue } from '@shared/schemas/job';
import type { ListResponse } from '@shared/schemas/pagination';

/*
 * The employee portal's queries. ROADMAP.md §6.
 *
 * These go through `@/lib/api`, which throws on any non-2xx — so a failed
 * request arrives as `error` and renders as an error, never as an empty list.
 * **That is P22, and P22 was found on this portal**: the prototype's
 * `/employee/clients/:id/jobs` returns a 500 and the screen shows "No jobs found
 * for this client" beside a header reading "2 Total Jobs". Nothing in here reads
 * `data ?? []` without also asking whether the request succeeded.
 */

export const EMPLOYEE_ROOT = ['employee'] as const;

export function useEmployeeDashboard() {
  return useQuery({
    queryKey: [...EMPLOYEE_ROOT, 'dashboard'],
    queryFn: () => api.get<EmployeeDashboard>('/api/employee/dashboard'),
  });
}

export interface EmployeeJobListParams {
  page: number;
  status: JobStatusValue | 'all';
}

export function useMyJobs(params: EmployeeJobListParams) {
  return useQuery({
    queryKey: [...EMPLOYEE_ROOT, 'jobs', 'list', params],
    queryFn: () =>
      api.get<ListResponse<EmployeeJobListItem>>(
        `/api/employee/jobs${queryString({
          page: params.page,
          status: params.status === 'all' ? undefined : params.status,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

/* Job detail, and the two lifecycle actions — ROADMAP.md §6.2, §6.3. */

function detailKey(id: string) {
  return [...EMPLOYEE_ROOT, 'jobs', 'detail', id] as const;
}

/** Everything derived from a job — the dashboard counts, the lists, the card. */
function invalidateAll(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: EMPLOYEE_ROOT });
}

export function useMyJob(id: string | undefined) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.get<EmployeeJobDetail>(`/api/employee/jobs/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

/**
 * Start Job, **optimistically** — ROADMAP.md §6.3 asks for this by name, and the
 * reason it gives is the right one: reception in a basement is unreliable, and a
 * cleaner who taps Start and sees nothing happen taps it again.
 *
 * So the cache moves to `in_progress` immediately and the row is put back if the
 * server refuses. The rollback is the half that matters — an optimistic update
 * with no rollback is a screen that lies about a job it never started, which on
 * this portal means somebody works a job the office still thinks is scheduled.
 *
 * **Not clock-in.** This changes the job; clocking in starts a shift and covers
 * several jobs (6.5).
 */
export function useStartMyJob(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<EmployeeJobDetail>(`/api/employee/jobs/${id}/start`),

    onMutate: async () => {
      // Cancel first, or an in-flight refetch lands after this write and undoes
      // it — which looks exactly like the mutation having failed.
      await queryClient.cancelQueries({ queryKey: detailKey(id) });
      const previous = queryClient.getQueryData<EmployeeJobDetail>(detailKey(id));

      if (previous) {
        queryClient.setQueryData<EmployeeJobDetail>(detailKey(id), {
          ...previous,
          status: 'in_progress',
          statusLabel: JOB_STATUS_LABELS.in_progress,
          startedAt: new Date().toISOString(),
          canStart: false,
        });
      }

      return { previous };
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey(id), context.previous);
    },

    // Whether it worked or not, the server's answer is the one that counts —
    // including `startedAt`, which the optimistic write guessed.
    onSettled: () => invalidateAll(queryClient),
  });
}

/** Finish — refused while a required task has no photo, in the server's words. */
export function useCompleteMyJob(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<EmployeeJobDetail>(`/api/employee/jobs/${id}/complete`),
    onSuccess: (detail) => {
      queryClient.setQueryData(detailKey(id), detail);
      return invalidateAll(queryClient);
    },
  });
}

/* My schedule — ROADMAP.md §6.6. */

/**
 * One bounded window. **The view decides the range**, not a page size — the
 * week asks for seven days and the month grid for its forty-two, which is the
 * P8 fix the admin calendar already follows.
 */
export function useMySchedule(range: { from: string; to: string }) {
  return useQuery({
    queryKey: [...EMPLOYEE_ROOT, 'schedule', range],
    queryFn: () => api.get<EmployeeSchedule>(`/api/employee/schedule${queryString(range)}`),
    placeholderData: (previous) => previous,
  });
}

/* Timekeeping — ROADMAP.md §6.5. */

/**
 * Clock in. **Not Start Job** — this begins a shift, and one shift covers
 * several jobs (FINDINGS.md §11.2).
 *
 * Not optimistic, unlike Start Job. A shift's start time is a payroll figure and
 * the server writes it from its own clock; guessing it in the cache would show
 * the cleaner a timer that jumps the moment the real answer arrives. The button
 * says "Clocking in…" instead, which is honest about the half-second.
 */
export function useClockIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ClockInInput) => api.post<OpenShift>('/api/employee/time/clock-in', input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/** Clock out. Refused when nothing is open, in the server's own words. */
export function useClockOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<ShiftView>('/api/employee/time/clock-out'),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/* My profile — ROADMAP.md §6.7. */

export function useMyProfile() {
  return useQuery({
    queryKey: [...EMPLOYEE_ROOT, 'profile'],
    queryFn: () => api.get<EmployeeProfileView>('/api/employee/profile'),
  });
}

/** Name, phone and address. **Nothing else is sendable** — see the schema. */
export function useUpdateMyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeProfileUpdateInput) =>
      api.patch<EmployeeProfileView>('/api/employee/profile', input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useSetEmployeeRecoveryEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recoveryEmail: string) =>
      api.put<EmployeeProfileView>('/api/employee/profile/recovery-email', { recoveryEmail }),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useEmployeeNotificationPreferences() {
  return useQuery({
    queryKey: [...EMPLOYEE_ROOT, 'notification-preferences'],
    queryFn: () =>
      api.get<EmployeeNotificationPreferences>('/api/employee/profile/notification-preferences'),
  });
}

export function useSetEmployeeNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeNotificationPreferenceInput) =>
      api.put<EmployeeNotificationPreferences>(
        '/api/employee/profile/notification-preferences',
        input,
      ),
    onSuccess: (data) => {
      // Written straight into the cache as well as invalidated: a toggle that
      // visibly springs back while a refetch lands reads as the save failing.
      queryClient.setQueryData([...EMPLOYEE_ROOT, 'notification-preferences'], data);
      return invalidateAll(queryClient);
    },
  });
}

/* Client lookup — ROADMAP.md §6.7, question Q5. */

export function useEmployeeClients(params: { page: number; search: string }) {
  return useQuery({
    queryKey: [...EMPLOYEE_ROOT, 'clients', params],
    queryFn: () =>
      api.get<ListResponse<EmployeeClientListItem>>(
        `/api/employee/clients${queryString({ page: params.page, search: params.search })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * One client, with the jobs *and* the count in the same response.
 *
 * **This is the P22 endpoint.** In the prototype the count and the list were two
 * requests, one of them 500'd, and the screen showed "No jobs found for this
 * client" beside "2 Total Jobs". One query means they cannot disagree, and
 * `api.ts` throwing means a failure renders as an error rather than as nothing.
 */
export function useEmployeeClient(id: string | undefined) {
  return useQuery({
    queryKey: [...EMPLOYEE_ROOT, 'clients', 'detail', id],
    queryFn: () => api.get<EmployeeClientDetail>(`/api/employee/clients/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

export function useAddClientNote(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<EmployeeClientDetail>(`/api/employee/clients/${id}/notes`, { body }),
    onSuccess: (detail) => {
      queryClient.setQueryData([...EMPLOYEE_ROOT, 'clients', 'detail', id], detail);
      return invalidateAll(queryClient);
    },
  });
}

/*
 * **Photos are not here.** They go through `lib/upload-queue.ts`, which is the
 * only path — a direct mutation lived here briefly in 6.4 and was deleted when
 * the queue landed, because two ways to upload a photo is one way that quietly
 * loses one on a bad connection. The queue owns the retry rules, and
 * `hooks/upload-queue.ts` is how React sees it.
 */
